/* Brief 3.5 PR 7-A — mechanical file split. Lifted verbatim from
   AdminMerchantDetail.jsx so the lint gate (PR 7-B) runs on small,
   focused files. No behaviour change. */
import { useEffect, useState } from "react";
import { Loader2, ScanLine, Copy } from "lucide-react";
import api from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { CollapsibleSection, fmtRelative } from "./primitives";

// ---------- H. Diagnostic (super_admin only) ----------

/**
 * Read-only merchant diagnostic — wraps `scripts/diagnose_merchant.py`
 * through `GET /api/admin/merchants/:id/diagnostic`. Surfaces the same
 * output an Emergent Support shell would produce, so super_admins can
 * self-serve "what's wrong with Genesis?" without waiting on a ticket.
 *
 * Gated to super_admin in the UI (the backend is gated too — UI hide
 * is purely so other admins don't see a button that 403s).
 */
export function DiagnosticPanel({ merchantId }) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [generatedAt, setGeneratedAt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  if (user?.role !== "super_admin") return null;

  const runDiagnostic = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/admin/merchants/${merchantId}/diagnostic`);
      setText(data.diagnostic || "");
      setGeneratedAt(data.generated_at || "");
    } catch (e) {
      setError(e?.response?.data?.detail || "Diagnostic failed");
    } finally {
      setLoading(false);
    }
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked — user can still select+copy manually */ }
  };

  return (
    <CollapsibleSection
      icon={ScanLine}
      title="Diagnostic (super-admin)"
      testId="merchant-section-diagnostic"
      defaultOpen={false}
    >
      <div className="mt-4 space-y-3">
        {/* neptune-allow: super-admin diagnostic description; intentionally names the engineer-flavoured step_data shape because this panel surfaces raw internal structure for debugging */}
        <p className="text-[12px] text-zinc-500 leading-relaxed">
          Runs the same read-only diagnostic as the CLI. Shows step_data shape,
          screening run state, document hashes, bundle detection, and known
          bug signatures. Output is admin-only — don&apos;t share verbatim with
          merchants.
        </p>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={runDiagnostic}
            disabled={loading}
            data-testid="merchant-diagnostic-run-btn"
            className="inline-flex items-center gap-2 rounded-lg border border-cyan-400/30 bg-cyan-400/[0.06] px-3 py-1.5 text-[12px] text-cyan-200 hover:bg-cyan-400/[0.12] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ScanLine className="h-3.5 w-3.5" />}
            {text ? "Re-run diagnostic" : "Run diagnostic"}
          </button>

          {text && (
            <button
              type="button"
              onClick={copyText}
              data-testid="merchant-diagnostic-copy-btn"
              className="inline-flex items-center gap-2 rounded-lg border border-white/[0.10] bg-white/[0.02] px-3 py-1.5 text-[12px] text-zinc-300 hover:bg-white/[0.06] transition-colors"
            >
              <Copy className="h-3.5 w-3.5" />
              {copied ? "Copied" : "Copy output"}
            </button>
          )}

          {generatedAt && (
            <span className="text-[11px] text-zinc-600 tabular-nums">
              Generated {fmtRelative(generatedAt) || generatedAt}
            </span>
          )}
        </div>

        {error && (
          <div data-testid="merchant-diagnostic-error" className="rounded-lg border border-rose-400/20 bg-rose-400/[0.04] px-3 py-2 text-[12px] text-rose-200">
            {String(error)}
          </div>
        )}

        {text && (
          <pre
            data-testid="merchant-diagnostic-output"
            className="rounded-lg border border-white/[0.06] bg-black/30 p-4 text-[11.5px] text-zinc-300 font-mono whitespace-pre-wrap break-words max-h-[600px] overflow-auto"
          >
            {text}
          </pre>
        )}
      </div>
    </CollapsibleSection>
  );
}
